// Build's Integrations page. Lives under Settings. Mirrors SNDash's tile UX —
// each tile = one external system Build talks to (plus the on-server Local
// GeoHub). The GeoHub tile expands to show the upstream feeds actually loaded
// on this server (buildings/roads/etc. per state).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface Feed { name: string; detail: string; }

interface Tile {
  system: string;
  label: string;
  description: string;
  category: 'live' | 'roadmap';
  connected: boolean;
  endpoint?: string;
  latency_ms?: number;
  details?: Record<string, any>;
  error?: string;
}

const SYSTEM_META: Record<string, { icon: string; accent: string }> = {
  milestone_core:  { icon: '🟦', accent: '#3b82f6' },
  geohub:          { icon: '🗺️', accent: '#f59e0b' },
  vetro:           { icon: '🌐', accent: '#0ea5e9' },
  tauc:            { icon: '📶', accent: '#7c3aed' },
  quickbooks:      { icon: '💚', accent: '#22c55e' },
  readynet:        { icon: '⚡', accent: '#10b981' },
  docusign:        { icon: '📄', accent: '#fbbf24' },
  box_sharepoint:  { icon: '📦', accent: '#a855f7' },
  state_portals:   { icon: '🌐', accent: '#0ea5e9' },
};

// Systems that have token-based credentials in integration_credential.
// Tile shows Connect / Rotate / Test / Disconnect buttons for these.
const TOKEN_AUTH_SYSTEMS = new Set(['vetro', 'tauc']);

// Systems that need a multi-field credential modal instead of a single
// token textarea. Map: system → list of fields. Fields marked `secret: true`
// get bundled into the encrypted token (as a JSON object); the rest go to
// the un-encrypted `metadata` blob (visible at rest, useful for things like
// base URLs that aren't sensitive).
const MULTI_FIELD_CONFIGS: Record<string, Array<{ key: string; label: string; placeholder?: string; secret?: boolean; help?: string }>> = {
  tauc: [
    { key: 'base_url',       label: 'API Base URL', placeholder: 'https://use1-tauc-northbound.tplinkcloud.com',
      help: 'Region-specific TAUC northbound endpoint (TP-Link gives this to you with your partner account).' },
    { key: 'tenant_id',      label: 'Tenant / Org ID', placeholder: 'e.g. 4f3c-89a2-…' },
    { key: 'client_id',      label: 'OAuth Client ID', secret: true, placeholder: '…' },
    { key: 'client_secret',  label: 'OAuth Client Secret', secret: true, placeholder: '…' },
  ],
};

function fmt(n: number | undefined | null): string {
  if (n === undefined || n === null) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toLocaleString();
}

export default function IntegrationsPage() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading, refetch } = useQuery<{ tiles: Tile[] }>({
    queryKey: ['integrations-status'],
    queryFn: () => api.get('/integrations/status'),
    refetchInterval: 30_000,
  });

  const tiles = data?.tiles ?? [];
  const live    = tiles.filter(t => t.category === 'live');
  const roadmap = tiles.filter(t => t.category === 'roadmap');

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Integrations</h1>
        <button className="btn btn-ghost btn-sm" onClick={() => refetch()}>↻ Refresh</button>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 4, maxWidth: 720 }}>
        External systems and on-server data sources Milestone Build uses for source-of-truth data and downstream sync. Status auto-refreshes every 30 seconds.
      </p>

      {isLoading && <div style={{ marginTop: 24, color: '#64748b' }}>Loading…</div>}

      {/* LIVE tiles */}
      {live.length > 0 && (
        <>
          <SectionHeader label="Live" subtle="Connected and exchanging data" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
            {live.map(t => (
              <TileCard
                key={t.system}
                tile={t}
                expanded={!!expanded[t.system]}
                onToggle={() => setExpanded(s => ({ ...s, [t.system]: !s[t.system] }))}
              />
            ))}
          </div>
        </>
      )}

      {/* ROADMAP tiles */}
      {roadmap.length > 0 && (
        <>
          <SectionHeader label="Roadmap" subtle="Partnership pending or build planned" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
            {roadmap.map(t => (
              <TileCard key={t.system} tile={t} expanded={false} onToggle={() => {}} roadmap />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ label, subtle }: { label: string; subtle: string }) {
  return (
    <div style={{ marginTop: 28, marginBottom: 10, display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#334155' }}>{label}</h2>
      <span style={{ fontSize: 11, color: '#94a3b8' }}>{subtle}</span>
    </div>
  );
}

function ReadyNetConnect() {
  const qc = useQueryClient();
  const { data: st } = useQuery<any>({ queryKey: ['ready-status'], queryFn: () => api.get('/ready/status'), refetchOnWindowFocus: true });
  // Per-tenant endpoints + the redirect URI derived from this box's own hostname.
  const { data: cfg } = useQuery<any>({ queryKey: ['ready-settings'], queryFn: () => api.get('/ready/settings') });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string>('');
  const [showCfg, setShowCfg] = useState(false);
  const [portal, setPortal] = useState('');
  const [graphql, setGraphql] = useState('');
  const [cfgMsg, setCfgMsg] = useState('');
  useEffect(() => {
    if (cfg?.portal_url && !portal) setPortal(cfg.portal_url);
    if (cfg?.graphql_url && !graphql) setGraphql(cfg.graphql_url);
  }, [cfg?.portal_url, cfg?.graphql_url]);
  const saveCfg = async () => {
    setBusy(true); setCfgMsg('');
    try {
      await api.put('/ready/settings', { portal_url: portal.trim(), graphql_url: graphql.trim() });
      setCfgMsg('Saved.'); qc.invalidateQueries({ queryKey: ['ready-settings'] });
    } catch (e: any) { setCfgMsg('Error: ' + (e?.message ?? e)); }
    finally { setBusy(false); }
  };
  const cfgPanel = (
    <div style={{ width: 340, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, textAlign: 'left' }}>
      <div style={{ fontSize: 10, color: '#475569', marginBottom: 6 }}>
        Your Ready endpoints (your state's portal). Register this exact redirect with Ready:
        <div style={{ marginTop: 4 }}>
          {cfg?.redirect_uri
            ? <code style={{ fontSize: 10, background: '#e2e8f0', padding: '2px 5px', borderRadius: 3, wordBreak: 'break-all' }}>{cfg.redirect_uri}</code>
            : <span style={{ color: '#b45309' }}>Set your hostname in Settings → Deployment → Hostname &amp; HTTPS first — Ready needs an exact https:// redirect.</span>}
        </div>
      </div>
      <input value={portal} onChange={(e) => setPortal(e.target.value)} placeholder="https://your-state.portal.ready.net"
        style={{ width: '100%', fontSize: 11, padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 3, marginBottom: 5 }} />
      <input value={graphql} onChange={(e) => setGraphql(e.target.value)} placeholder="https://grapi.ready.net/graphql"
        style={{ width: '100%', fontSize: 11, padding: '5px 7px', border: '1px solid #cbd5e1', borderRadius: 3, marginBottom: 6 }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button onClick={saveCfg} disabled={busy || !portal.trim() || !graphql.trim()}
          style={{ fontSize: 11, padding: '5px 12px', border: 'none', borderRadius: 4, background: '#0f172a', color: '#fff', cursor: 'pointer' }}>Save endpoints</button>
        {cfgMsg && <span style={{ fontSize: 10, color: cfgMsg.startsWith('Error') ? '#b91c1c' : '#047857' }}>{cfgMsg}</span>}
      </div>
    </div>
  );
  const connect = async () => {
    setBusy(true);
    try { const r: any = await api.post('/ready/oauth/start', {}); if (r?.authorize_url) window.location.href = r.authorize_url; }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setTestResult('Testing…');
    try { const r: any = await api.post('/ready/test', {}); setTestResult(r?.ok ? 'OK — API responded' : 'Error: ' + JSON.stringify(r?.errors ?? r).slice(0, 160)); }
    catch (e: any) { setTestResult('Error: ' + (e?.message ?? e)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post('/ready/disconnect', {}); setTestResult(''); qc.invalidateQueries({ queryKey: ['ready-status'] }); }
    finally { setBusy(false); }
  };
  if (!st) return <span style={{ fontSize: 11, color: '#94a3b8' }}>…</span>;
  if (!st.connected) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
      {showCfg && cfgPanel}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => setShowCfg((v) => !v)} style={{ fontSize: 11, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
          {showCfg ? 'Hide setup' : 'Endpoints'}
        </button>
        <button onClick={connect} disabled={busy || cfg?.ready_to_connect === false}
          title={cfg?.needs_hostname ? 'Set your hostname & HTTPS first' : ''}
          style={{ fontSize: 11, padding: '6px 14px', border: 'none', borderRadius: 4, background: cfg?.ready_to_connect === false ? '#94a3b8' : '#10b981', color: '#fff', fontWeight: 700, cursor: busy || cfg?.ready_to_connect === false ? 'default' : 'pointer' }}>
          {busy ? '…' : 'Connect to Ready.net'}
        </button>
      </div>
      <span style={{ fontSize: 10, color: '#94a3b8' }}>
        {cfg?.needs_hostname ? 'Set your hostname & HTTPS first' : "OAuth · you sign in at Ready's portal"}
      </span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
      <span style={{ fontSize: 11, color: '#047857', fontWeight: 700 }}>Connected{st.user?.email ? ' — ' + st.user.email : ''}{st.applicant_id ? ' · applicant ' + String(st.applicant_id).slice(0, 8) : ''}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={test} disabled={busy} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>Test connection</button>
        <button onClick={() => setShowCfg((v) => !v)} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>Endpoints</button>
        <button onClick={disconnect} disabled={busy} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #fca5a5', borderRadius: 4, background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Disconnect</button>
      </div>
      {showCfg && cfgPanel}
      {testResult && <span style={{ fontSize: 10, color: '#475569', maxWidth: 280, textAlign: 'right' }}>{testResult}</span>}
    </div>
  );
}

function TileCard({ tile, expanded, onToggle, roadmap }: {
  tile: Tile; expanded: boolean; onToggle: () => void; roadmap?: boolean;
}) {
  const meta = SYSTEM_META[tile.system] ?? { icon: '•', accent: '#94a3b8' };
  const isLocalGeohub = tile.system === 'geohub';
  const feeds: Feed[] = Array.isArray(tile.details?.feeds) ? tile.details!.feeds : [];
  const status = roadmap
    ? { color: '#94a3b8', bg: '#f1f5f9', text: tile.system === 'readynet' ? 'Pending Partnership' : 'Roadmap' }
    : tile.connected
      ? { color: '#047857', bg: '#dcfce7', text: isLocalGeohub ? 'Local — active' : 'Connected' }
      : { color: '#b91c1c', bg: '#fee2e2', text: 'Disconnected' };

  return (
    <div style={{
      border: `1px solid ${roadmap ? '#e2e8f0' : (tile.connected ? '#86efac' : '#fca5a5')}`,
      borderRadius: 8,
      padding: 16,
      background: roadmap ? '#fafafa' : '#fff',
      opacity: roadmap ? 0.85 : 1,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flex: 1 }}>
          <div style={{ fontSize: 26, lineHeight: 1 }}>{meta.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{tile.label}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>{tile.description}</div>
          </div>
        </div>
        <span style={{
          fontSize: 10,
          padding: '3px 8px',
          borderRadius: 4,
          fontWeight: 700,
          background: status.bg,
          color: status.color,
          whiteSpace: 'nowrap',
        }}>
          {status.text}
        </span>
      </div>

      {/* Details */}
      {!roadmap && tile.connected && (
        <>
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 4, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 11 }}>
            {isLocalGeohub ? (
              <>
                <span style={{ color: '#64748b' }}>Source</span>
                <span style={{ color: '#1e293b' }}>On-server (footprints DB) — no external host</span>
                <span style={{ color: '#64748b' }}>States loaded</span>
                <span>{(tile.details?.states ?? []).length ? (tile.details!.states as string[]).join(', ') : '—'}</span>
                <span style={{ color: '#64748b' }}>Total features</span>
                <span>{fmt(tile.details?.total_features)}</span>
              </>
            ) : (
              <>
                <span style={{ color: '#64748b' }}>Endpoint</span>
                <span style={{ fontFamily: 'monospace', color: '#1e293b' }}>{tile.endpoint}</span>
                <span style={{ color: '#64748b' }}>Latency</span>
                <span style={{ color: tile.latency_ms && tile.latency_ms > 1000 ? '#b45309' : '#10b981' }}>
                  {tile.latency_ms ? `${tile.latency_ms} ms` : '—'}
                </span>
                {tile.details?.buildings !== undefined && (
                  <>
                    <span style={{ color: '#64748b' }}>Buildings</span>
                    <span>{fmt(tile.details.buildings)}</span>
                  </>
                )}
                {tile.details?.fiber_routes !== undefined && (
                  <>
                    <span style={{ color: '#64748b' }}>Fiber routes</span>
                    <span>{fmt(tile.details.fiber_routes)}</span>
                  </>
                )}
              </>
            )}
          </div>

          {/* GeoHub upstream feeds — the layers actually loaded on this server */}
          {isLocalGeohub && feeds.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={onToggle}
                style={{ background: 'none', border: 'none', color: '#0f766e', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                {expanded ? '▾' : '▸'} Loaded data feeds ({feeds.length})
              </button>
              {expanded && (
                <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff7ed', borderRadius: 4, border: '1px solid #fed7aa' }}>
                  {feeds.map(f => (
                    <div key={f.name} style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 8, fontSize: 11, padding: '3px 0' }}>
                      <span style={{ fontWeight: 600, color: '#9a3412' }}>{f.name}</span>
                      <span style={{ color: '#475569' }}>{f.detail}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!roadmap && !tile.connected && tile.error && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 11, fontFamily: 'monospace' }}>
          {tile.error}
        </div>
      )}

      {/* Action row */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        {roadmap ? (
          tile.system === 'readynet' ? (
            <ReadyNetConnect />
          ) : (
            <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
              Coming in a future release
            </span>
          )
        ) : (
          <>
            {isLocalGeohub && (
              <a href="/settings/deployment" style={{
                fontSize: 11, padding: '5px 12px', border: '1px solid #cbd5e1',
                borderRadius: 4, color: '#0f172a', textDecoration: 'none', background: '#fff',
              }}>
                Manage on Deployment →
              </a>
            )}
            {tile.system === 'milestone_core' && (
              <a href="/bead-compliance/reimbursement" style={{
                fontSize: 11, padding: '5px 12px', border: '1px solid #cbd5e1',
                borderRadius: 4, color: '#0f172a', textDecoration: 'none', background: '#fff',
              }}>
                Map QBO → BEAD categories
              </a>
            )}
            {TOKEN_AUTH_SYSTEMS.has(tile.system) && (
              <CredentialControls system={tile.system} connected={tile.connected} />
            )}
            {tile.endpoint && !isLocalGeohub && (
              <a href={tile.endpoint} target="_blank" rel="noreferrer" style={{
                fontSize: 11, padding: '5px 12px', border: '1px solid #cbd5e1',
                borderRadius: 4, color: '#0f172a', textDecoration: 'none', background: '#fff',
              }}>
                Open ↗
              </a>
            )}
          </>
        )}
      </div>

      {/* Vetro-specific extra detail row */}
      {!roadmap && tile.connected && tile.system === 'vetro' && tile.details && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 4, fontSize: 11, color: '#155e75' }}>
          Authenticated as org #{tile.details.org_id} · {tile.details.create_layer_count} writable layers
        </div>
      )}
    </div>
  );
}

// ─── Credential management UI ──────────────────────────────────────────────
// "Connect" / "Rotate" / "Test now" / "Disconnect" buttons + token-entry modal.
// The token textarea is the ONLY place a plaintext token ever exists in the
// frontend; it's submitted via PUT /credentials/:system and never re-displayed.

function CredentialControls({ system, connected }: { system: string; connected: boolean }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const testMut = useMutation({
    mutationFn: () => api.post(`/integrations/credentials/${system}/test`, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['integrations-status'] }),
  });

  const disconnectMut = useMutation({
    mutationFn: () => api.del(`/integrations/credentials/${system}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['integrations-status'] }),
  });

  const btnStyle = {
    fontSize: 11, padding: '5px 12px',
    border: '1px solid #cbd5e1', borderRadius: 4,
    background: '#fff', color: '#0f172a', cursor: 'pointer',
  } as const;

  return (
    <>
      {connected && (
        <button
          onClick={() => testMut.mutate()}
          disabled={testMut.isPending}
          style={btnStyle}
          title="Re-test credential against the API"
        >
          {testMut.isPending ? 'Testing…' : '↻ Test now'}
        </button>
      )}
      <button
        onClick={() => setOpen(true)}
        style={{ ...btnStyle, background: '#1e293b', color: '#fff', borderColor: '#1e293b' }}
      >
        {connected ? 'Rotate token' : 'Connect'}
      </button>
      {connected && (
        <button
          onClick={() => {
            if (confirm(`Disconnect ${system}? This clears the stored token but keeps the audit trail.`)) {
              disconnectMut.mutate();
            }
          }}
          disabled={disconnectMut.isPending}
          style={{ ...btnStyle, color: '#b91c1c', borderColor: '#fca5a5' }}
        >
          {disconnectMut.isPending ? 'Disconnecting…' : 'Disconnect'}
        </button>
      )}
      {open && (
        <CredentialModal
          system={system}
          mode={connected ? 'rotate' : 'connect'}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ['integrations-status'] });
          }}
        />
      )}
    </>
  );
}

function CredentialModal({ system, mode, onClose, onSaved }: {
  system: string;
  mode: 'connect' | 'rotate';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isMultiField = !!MULTI_FIELD_CONFIGS[system];
  const fields = MULTI_FIELD_CONFIGS[system] ?? [];

  // Single-token mode (Vetro): everything in one textarea.
  const [token, setToken] = useState('');

  // Multi-field mode (TAUC): one input per field. Secret fields land in token;
  // non-secret in metadata.
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(fields.map(f => [f.key, '']))
  );

  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      if (isMultiField) {
        const secretFields = fields.filter(f => f.secret);
        const publicFields = fields.filter(f => !f.secret);
        const secretBlob = Object.fromEntries(secretFields.map(f => [f.key, values[f.key].trim()]));
        const metadata = Object.fromEntries(publicFields.map(f => [f.key, values[f.key].trim()]));
        return api.put(`/integrations/credentials/${system}`, {
          token: JSON.stringify(secretBlob),
          metadata,
        });
      }
      return api.put(`/integrations/credentials/${system}`, { token: token.trim() });
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    },
  });

  const sysLabel: Record<string, { name: string; help: string }> = {
    vetro: {
      name: 'VETRO FiberMap',
      help: 'Find your token in VETRO → User Dashboard → API Access. We test the token against the live API before saving — a bad token is rejected.',
    },
    tauc: {
      name: 'TP-Link TAUC',
      help: 'Mint an OAuth2 client in your TP-Link Service Provider partner portal under TAUC → Open API. We test all four fields against the live API before saving — a bad credential is rejected. The IXP-qualification check happens after the test passes; see the tile for results.',
    },
  };
  const meta = sysLabel[system] ?? { name: system, help: 'Paste your API token below.' };

  const canSubmit = isMultiField
    ? fields.every(f => values[f.key].trim().length > 0)
    : token.trim().length > 0;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 540, maxWidth: '95%', background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
          {mode === 'rotate' ? 'Rotate token' : 'Connect'} · {meta.name}
        </h3>
        <p style={{ marginTop: 6, fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
          {meta.help}
        </p>
        {isMultiField ? (
          // Multi-field mode (TAUC etc.) — one input per credential field.
          // Secret fields get bundled into encrypted `token`; non-secret go
          // to `metadata` (cleartext at rest).
          fields.map((f) => (
            <label key={f.key} style={{ display: 'block', marginTop: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
                {f.label}
                {f.secret && <span style={{ background: '#1e3a8a', color: '#fff', padding: '0 5px', fontSize: 9, borderRadius: 3 }}>SECRET</span>}
              </span>
              <input
                type={f.secret ? 'password' : 'text'}
                value={values[f.key]}
                onChange={(e) => { setValues((s) => ({ ...s, [f.key]: e.target.value })); setError(null); }}
                placeholder={f.placeholder}
                spellCheck={false}
                style={{
                  width: '100%', marginTop: 4, padding: '8px 10px', fontSize: 12,
                  fontFamily: f.secret ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
                  border: '1px solid #cbd5e1', borderRadius: 4,
                }}
              />
              {f.help && <div style={{ fontSize: 10, color: '#64748b', marginTop: 3, lineHeight: 1.4 }}>{f.help}</div>}
            </label>
          ))
        ) : (
          <label style={{ display: 'block', marginTop: 14 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              API token
            </span>
            <textarea
              value={token}
              onChange={e => { setToken(e.target.value); setError(null); }}
              rows={4}
              placeholder="eyJhbGciOiJIUzI1NiIs…"
              spellCheck={false}
              style={{
                width: '100%', marginTop: 4, padding: 10, fontSize: 11,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                border: '1px solid #cbd5e1', borderRadius: 4, resize: 'vertical',
              }}
            />
          </label>
        )}
        {error && (
          <div style={{ marginTop: 8, padding: '6px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 11 }}>
            {error}
          </div>
        )}
        <div style={{ marginTop: 14, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: 11, color: '#92400e' }}>
          Token is encrypted at rest with AES-256-GCM. Once saved, it can't be viewed again — only rotated or cleared.
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={!canSubmit || saveMut.isPending}
            style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer' }}
          >
            {saveMut.isPending ? 'Testing & saving…' : (mode === 'rotate' ? 'Rotate' : 'Connect')}
          </button>
        </div>
      </div>
    </div>
  );
}
